import { useEffect } from 'react'
import { createApiClient } from './api'
import { LoginScreen } from './auth/LoginScreen'
import { useAuth } from './auth/useAuth'
import { PcCanvas } from './pc/PcCanvas'
import { isDrawCallHarnessEnabled } from './perf/drawCallHarness'
import { RemoteStateController } from './remote/RemoteStateController'
import { useArenaStore } from './store'
import { BuildingCallout } from './ui/BuildingCallout'
import { ClickToPlay } from './ui/ClickToPlay'
import { Crosshair } from './ui/Crosshair'
import { HUD } from './ui/HUD'
import { InteractionDialog } from './ui/InteractionDialog'
import { Minimap } from './ui/Minimap'
import { OnboardingHint } from './ui/OnboardingHint'
import { PauseMenu } from './ui/PauseMenu'
import { RunInspector } from './ui/RunInspector'
import { Toasts } from './ui/Toasts'
import { WorkplacePhone } from './ui/WorkplacePhone'
import { startWorldStateRuntime } from './world/worldStateRuntime'

const IS_MOCK = import.meta.env.VITE_MOCK_DATA === 'true'
const IS_DRAW_CALL_HARNESS = isDrawCallHarnessEnabled()

export function App() {
  const { status, user } = useAuth()
  const setApiClient = useArenaStore((s) => s.setApiClient)
  const apiClient = useArenaStore((s) => s.apiClient)

  useEffect(() => {
    if (IS_MOCK) {
      // Mock mode: bypass auth, use mock client
      setApiClient(createApiClient('mock'))
      useArenaStore.getState().setWorldStateStatus('disconnected')
      return
    }
    if (status === 'authenticated') {
      const userId = user?.sub
      const workspaceId = user?.workspaceId
      const projectId = user?.projectId
      const sessionBinding = user?.sessionBinding
      setApiClient(
        userId && workspaceId && projectId && sessionBinding
          ? createApiClient(undefined, { userId, workspaceId, projectId, sessionBinding })
          : null,
      )
    } else {
      setApiClient(null)
    }
  }, [status, user, setApiClient])

  useEffect(() => {
    if (!apiClient) return
    const controller = new RemoteStateController(apiClient, {
      continuous: !(IS_MOCK && IS_DRAW_CALL_HARNESS),
    })
    controller.start()
    return () => controller.stop()
  }, [apiClient])

  useEffect(() => {
    if (IS_MOCK || status !== 'authenticated' || !apiClient) return
    const generation = useArenaStore.getState().apiGeneration
    return startWorldStateRuntime({ apiClient, apiGeneration: generation })
  }, [apiClient, status])

  // Loading state
  if (!IS_MOCK && status === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(245,235,224,0.4)', fontSize: '0.875rem', fontFamily: "'Nunito', system-ui, sans-serif" }}>
        Initializing…
      </div>
    )
  }

  // Unauthenticated (only gate when not in mock mode)
  if (!IS_MOCK && status !== 'authenticated') {
    return <LoginScreen />
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <PcCanvas />
      <ClickToPlay />
      <Crosshair />
      <HUD />
      <Minimap />
      <BuildingCallout />
      <OnboardingHint />
      <PauseMenu />
      <InteractionDialog />
      <RunInspector />
      <Toasts />
      <WorkplacePhone />
    </div>
  )
}
